import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import useEventTracking from '@hooks/useEventTracking';
import useFollowUpdate from '@hooks/useFollowUpdate';
import { ClickTarget, click } from '@tracking';
import { FollowAction, FollowType } from '@utils/backend-api/follow';

import Button, { ButtonProps } from '../ButtonLegacy';
import Tooltip from '../Tooltip';
import styles from './styles.module.scss';

const EVOLVING_STORY_TOOLTIP_COPY = 'Use the "follow" button to watch how this article develops.';

const tooltipMessage = (
  isFollowing: boolean,
  name: string,
  tooltipType: FollowButtonProps['tooltipType']
) => {
  if (isFollowing) {
    switch (tooltipType) {
      case 'directory':
        return `You’re now following ${name}! You’ll receive emails when they update their profile, comment on articles and post in the Forum.`;
      case 'evolvingStory':
        return ''; // no message for evolving story when user is following
    }
  } else {
    switch (tooltipType) {
      case 'directory':
        return `Get notifications when ${name} is active on The Information.`;
      case 'evolvingStory':
        return EVOLVING_STORY_TOOLTIP_COPY;
    }
  }
};

interface followButtonProps {
  'data-test': string;
  id?: string;
  onClick: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  rounded?: ButtonProps['rounded'];
  variant?: ButtonProps['variant'];
  width?: ButtonProps['width'];
}

export type FollowButtonProps = {
  className?: string;
  followStatus: boolean;
  followeeId: number;
  followType: FollowType;
  showSubscribeModal?: boolean;
  username?: string;
  name?: string;
  tooltipEnabled?: boolean;
  tooltipType?: 'directory' | 'evolvingStory';
  // primary: blue follow button | secondary: red button (as seen on the events page)
  variant?: 'primary' | 'secondary' | 'link';
};

const FollowButton = ({
  className,
  followStatus,
  followeeId,
  followType,
  showSubscribeModal = false,
  username,
  name,
  tooltipEnabled = false,
  tooltipType = 'directory',
  variant = 'primary',
}: FollowButtonProps) => {
  const [text, setText] = useState(followStatus ? 'Following' : 'Follow');
  const [isFollowing, setIsFollowing] = useState(followStatus);
  const [showFollowTooltip, setShowFollowTooltip] = useState(false);
  const { mutateAsync: updateFollow } = useFollowUpdate();
  const queryClient = useQueryClient();
  const { trackEvent } = useEventTracking();

  useEffect(() => {
    setIsFollowing(followStatus);
    setText(followStatus ? 'Following' : 'Follow');
  }, [followStatus]);

  const handleFollow = async () => {
    if (showSubscribeModal) {
      handleLaunchModal();
      return;
    }

    updateFollow(
      { followeeId, followAction: FollowAction.Follow, followType, username, queryClient },
      {
        onSuccess: () => {
          setText('Following');
          setIsFollowing(true);
          trackEvent(click({ clickTarget: ClickTarget.Follow }));
          // remove tooltip for evolvingStory since there's no text user is following
          if (tooltipType === 'evolvingStory') setShowFollowTooltip(false);
        },
      }
    );
  };

  const handleUnfollow = async () => {
    updateFollow(
      { followeeId, followAction: FollowAction.Unfollow, followType, username, queryClient },
      {
        onSuccess: () => {
          setText('Follow');
          setIsFollowing(false);
          trackEvent(click({ clickTarget: ClickTarget.Unfollow }));
        },
      }
    );
  };

  const handleLaunchModal = () => {
    const immersiveArticleModalContainer = document.getElementById(
      'immersive-article-modal-container'
    );
    immersiveArticleModalContainer.classList.remove('!hidden');

    // Prevent clicks inside the modal from bubbling up to the modal container
    const immersiveArticleModal = document.getElementById('immersive-article-modal');
    immersiveArticleModal.addEventListener('click', (e) => e.stopPropagation());

    const modalcloseBtn = document.querySelector('.close-modal-btn-wrapper');
    [immersiveArticleModalContainer, modalcloseBtn].forEach((elt) => {
      elt.addEventListener('click', () => immersiveArticleModalContainer.classList.add('!hidden'));
    });
  };

  // general props for all variants of the follow button
  const followButtonProps: followButtonProps = {
    'data-test': 'follow-button',
    onClick: isFollowing ? handleUnfollow : handleFollow,
    onMouseEnter: () => {
      if (isFollowing) setText('Unfollow');
      // don't show tooltip for evolving story when user is following
      setShowFollowTooltip(!isFollowing || tooltipType !== 'evolvingStory');
    },
    onMouseLeave: () => {
      if (isFollowing) setText('Following');
      setShowFollowTooltip(false);
    },
  };

  // specific props to be passed to the ButtonLegacy component
  if (variant === 'primary') {
    followButtonProps.rounded = 'full';
    followButtonProps.variant = 'primary';
  } else if (variant === 'secondary') {
    followButtonProps.rounded = 'slight';
    followButtonProps.variant = 'primary';
    followButtonProps.width = 'medium';
  } else if (variant === 'link') {
    followButtonProps.variant = 'link';
  }

  return (
    <div className="relative">
      <Button
        id="FollowButton"
        className={clsx(styles.FollowButton, className || '', {
          [styles[`${variant}Button`]]: variant,
          [styles[`${variant}Following`]]: isFollowing,
        })}
        {...followButtonProps}
      >
        <span>{text}</span>
      </Button>
      <Tooltip
        message={tooltipMessage(isFollowing, name, tooltipType)}
        showTooltip={tooltipEnabled && showFollowTooltip}
      />
    </div>
  );
};

export default FollowButton;
